import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';


import { ArrowUpwardIcon } from '@ui/icons/material/ArrowUpward';
import { ArrowDownwardIcon } from '@ui/icons/material/ArrowDownward';
import { ArrowBackIcon } from '@ui/icons/material/ArrowBack';

import { FavoriteBorderIcon } from '@ui/icons/material/FavoriteBorder';
import { FavoriteIcon } from '@ui/icons/material/Favorite';
import { VisibilityIcon } from '@ui/icons/material/Visibility';
import { ShareIcon } from '@ui/icons/material/Share';
import { MessageIcon } from '@ui/icons/material/Message';
import { useAuth } from '@common/auth/use-auth';

import { useParams, useNavigate } from 'react-router-dom';

import CommentsDrawer from './VideoCommentsDialog';
import {toastState, useToastStore} from '@ui/toast/toast-store';

import VideoHelmet from './HelmetMetaVideo';
import { VideoProgress } from './VideoProgress';

interface Clip {
  id: number;
  clip_id: string;
  resolution_1280x720: string;
  resolution_854x480: string;
  video: string;
  title: string;
  description: string;
  username: string;
  avatar: string;
  views: number;
  likes: number;
  comments: number;
  sharing: number;
  size: number; // Adicionado videoSize como opcional
  thumbnail: string; // Adicionada propriedade thumbnail
  user_id: string;  // Adicione esta linha
}

interface ClipsResponse {
  status: string;
  clips: Clip[];
  hasMore: boolean;
  totalClips: number;
  currentPage: number;
}

// Add this type definition for route parameters
type RouteParams = {
  clipId: string;
}

export function ClipsListPage() {
  const { clipId } = useParams<keyof RouteParams>();
  const { user } = useAuth(); // Get logged in user
  const [clips, setClips] = useState<Clip[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [holdTimer, setHoldTimer] = useState<NodeJS.Timeout | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const preloadedVideos = useRef<Set<number>>(new Set());
  const initialTouchPos = useRef<{ x: number; y: number } | null>(null);
  const moveThreshold = 10;
  const [expandedDescription, setExpandedDescription] = useState<number | null>(null);
  const [expandedTitle, setExpandedTitle] = useState<number | null>(null);

  const [likedVideos, setLikedVideos] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState<number | null>(null);
   // Add navigation handler
   const handleBackClick = () => {
    navigate('/library/songs');
  };

  const [videoTimes, setVideoTimes] = useState<{ [key: number]: { current: number; duration: number } }>({});
const handleTimeUpdate = (index: number) => {
  const video = videoRefs.current[index];
  if (video) {
    setVideoTimes(prev => ({
      ...prev,
      [index]: {
        current: video.currentTime,
        duration: video.duration
      }
    }));
  }
};

const handleSeek = (index: number, time: number) => {
  const video = videoRefs.current[index];
  if (video) {
    video.currentTime = time;
  }
};
   // função para navegação do perfil
   const navigateToUserProfile = (userId: string, username: string) => {
    navigate(`/user/${userId}/${username}/clips`);
  };
  // Função para truncar a descrição
  const truncateText = (text: string, limit: number) => {
    if (text.length <= limit) return text;
    return text.slice(0, limit) + '...';
  };

  // Função para lidar com o like
    // Updated handleLike function to use logged in user's ID
    const handleLike = async (clipId: number) => {
      if (!user) {
        toast.danger(message('Please login to like videos'));
        return;
      }
  
      try {
        const response = await fetch('/clips-list.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'like',
            id: clipId,
            user_id: user.id,
          }),
        });
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
  
        const data = await response.json();
        
        if (data.status === 'success') {
          setClips(prevClips =>
            prevClips.map(clip =>
              clip.id === clipId
                ? { 
                    ...clip, 
                    likes: data.current_likes
                  }
                : clip
            )
          );
          
          // Toggle liked state
          setLikedVideos(prev => {
            const newSet = new Set(prev);
            if (data.action === 'liked') {
              newSet.add(clipId);
            } else {
              newSet.delete(clipId);
            }
            return newSet;
          });
          
          toast.positive(message(data.action === 'liked' ? '❤️ Vídeo curtido com sucesso!' : '💔😕 Deixou de curtir que pena.'));
        } else {
          toast.danger(message(data.message || '⚠️ Ouvi um erro ao curtir'));
        }
      } catch (error) {
        console.error('Erro do Like:', error);
        toast.danger(message('⚠️ Erro ao processar sua curtida. Por favor, tente novamente mais tarde.'));
      }
    };

  // Função para compartilhar
// Modified handleShare function with floating progress bar
const handleShare = async (clipId: string, videoTitle: string, size?: number) => {
  let abortController = new AbortController();
  
  try {
    // Create floating container for share options
    const floatingContainer = document.createElement('div');
    floatingContainer.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      padding: 12px 16px;
      border-radius: 12px;
      width: 90%;
      max-width: 400px;
      z-index: 9999;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      display: flex;
      flex-direction: column;
      gap: 10px;
    `;

    // Title
    const titleElement = document.createElement('div');
    titleElement.style.cssText = `
      color: white;
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 8px;
      text-align: center;
    `;
    titleElement.textContent = 'Compartilhar';

    // Buttons container
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;

    // Share Link button
    const shareLinkButton = document.createElement('button');
    shareLinkButton.textContent = 'Partilhar Link';
    shareLinkButton.style.cssText = `
      padding: 10px;
      background: #35383c;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      width: 100%;
    `;
    shareLinkButton.onmouseover = () => {
      shareLinkButton.style.opacity = '0.9';
    };
    shareLinkButton.onmouseout = () => {
      shareLinkButton.style.opacity = '1';
    };

    // Send Video button (only shown if video is <= 20MB)
    const sendVideoButton = document.createElement('button');
    sendVideoButton.textContent = 'Enviar Vídeo';
    sendVideoButton.style.cssText = `
      padding: 10px;
      background: #35383c;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      width: 100%;
      ${(!size || size > 20 * 1024 * 1024) ? 'display: none;' : ''}
    `;
    sendVideoButton.onmouseover = () => {
      sendVideoButton.style.opacity = '0.9';
    };
    sendVideoButton.onmouseout = () => {
      sendVideoButton.style.opacity = '1';
    };

    // Cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancelar';
    cancelButton.style.cssText = `
      padding: 10px;
      background: transparent;
      color: #ef4444;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      width: 100%;
      margin-top: 4px;
    `;
    cancelButton.onmouseover = () => {
      cancelButton.style.background = 'rgba(239, 68, 68, 0.1)';
    };
    cancelButton.onmouseout = () => {
      cancelButton.style.background = 'transparent';
    };

    // Assemble the container
    buttonContainer.appendChild(shareLinkButton);
    buttonContainer.appendChild(sendVideoButton);
    buttonContainer.appendChild(cancelButton);
    floatingContainer.appendChild(titleElement);
    floatingContainer.appendChild(buttonContainer);
    document.body.appendChild(floatingContainer);

    // Handle share link button click
    // Inside the share link button click handler, update this part:
    shareLinkButton.onclick = async () => {
      try {
        const shareData = {
          title: videoTitle,
          text: `Confira este vídeo na Sintonizy: ${videoTitle}`,
          url: `${window.location.origin}/zy/${clipId}`
        };

        if (navigator.share) {
          await navigator.share(shareData);
          
          // Register successful link share
          try {
            const shareResponse = await fetch('/share-video.php', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                clipId,
                shareType: 'link' // Add shareType to differentiate from video shares
              })
            });
            
            if (!shareResponse.ok) {
              console.error('Failed to register share');
            }
          } catch (error) {
            console.error('Error registering share:', error);
          }
          
          document.body.removeChild(floatingContainer);
          toast.positive(message('🔗 Link compartilhado com sucesso!'));
        } else {
          // Fallback to clipboard
          await navigator.clipboard.writeText(shareData.url);
          
          // Register successful clipboard share
          try {
            const shareResponse = await fetch('/share-video.php', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                clipId,
                shareType: 'link'
              })
            });
            
            if (!shareResponse.ok) {
              console.error('Failed to register share');
            }
          } catch (error) {
            console.error('Error registering share:', error);
          }
          
          document.body.removeChild(floatingContainer);
          toast.positive(message('🔗 Link copiado para área de transferência!'));
        }
      } catch (error) {
        console.error('Share link error:', error);
        toast.danger(message('⚠️ Erro ao compartilhar link. Tente novamente.'));
      }
    };

    // Handle send video button click
    sendVideoButton.onclick = async () => {
      document.body.removeChild(floatingContainer);
      
      // Create new container for download progress
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = floatingContainer.style.cssText;

      // Progress bar container
      const progressBarContainer = document.createElement('div');
      progressBarContainer.style.cssText = `
        width: 100%;
        height: 4px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
        overflow: hidden;
      `;

      // Progress bar
      const progressBar = document.createElement('div');
      progressBar.style.cssText = `
        width: 0%;
        height: 100%;
        background: #ff1c59;
        border-radius: 2px;
        transition: width 0.3s ease;
      `;
      progressBarContainer.appendChild(progressBar);

      // Progress text
      const progressText = document.createElement('div');
      progressText.style.cssText = `
        color: white;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        justify-content: space-between;
        align-items: center;
      `;
      progressText.innerHTML = '<span>Preparando vídeo</span><span>0%</span>';

      // Cancel button for download
      const downloadCancelButton = cancelButton.cloneNode(true) as HTMLButtonElement;
      downloadCancelButton.onclick = () => {
        abortController.abort();
        document.body.removeChild(progressContainer);
        toast.danger(message('Download cancelado'));
      };
  

      // Share button (initially hidden)
      const shareButton = document.createElement('button');
      shareButton.textContent = 'Compartilhar';
      shareButton.style.cssText = `
        padding: 6px 12px;
        background: #35383c;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s ease;
        display: none;
      `;

      // Buttons container for download progress
      const downloadButtonContainer = document.createElement('div');
      downloadButtonContainer.style.cssText = `
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        margin-top: 4px;
      `;
      downloadButtonContainer.appendChild(downloadCancelButton);
      downloadButtonContainer.appendChild(shareButton);

      // Assemble the progress container
      progressContainer.appendChild(progressText);
      progressContainer.appendChild(progressBarContainer);
      progressContainer.appendChild(downloadButtonContainer);
      document.body.appendChild(progressContainer);

      try {
        // Start the download process
        const response = await fetch('/share-video.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ clipId }),
          signal: abortController.signal
        });
        
        if (!response.ok) {
          throw new Error('⚠️ Falha ao preparar o vídeo');
        }
        
        const data = await response.json();
        
        if (data.status === 'error') {
          throw new Error(data.message);
        }

        // Download video with progress
        const videoResponse = await fetch(data.downloadUrl, { 
          signal: abortController.signal 
        });
        const totalSize = Number(videoResponse.headers.get('content-length'));
        
        if (!videoResponse.body) {
          throw new Error('Failed to fetch video data');
        }

        const reader = videoResponse.body.getReader();
        const chunks: Uint8Array[] = [];
        let receivedLength = 0;

        while(true) {
          const {done, value} = await reader.read();
          
          if (done) {
            break;
          }
          
          chunks.push(value);
          receivedLength += value.length;
          
          const progress = (receivedLength / totalSize) * 100;
          const progressPercentage = Math.round(progress);
          progressBar.style.width = `${progressPercentage}%`;
          progressText.innerHTML = `<span>Preparando vídeo</span><span>${progressPercentage}%</span>`;
        }
        
        const chunksAll = new Uint8Array(receivedLength);
        let position = 0;
        for(let chunk of chunks) {
          chunksAll.set(chunk, position);
          position += chunk.length;
        }
        
        const videoBlob = new Blob([chunksAll], { type: 'video/mp4' });
        const videoFile = new File([videoBlob], data.filename, {
          type: 'video/mp4'
        });

        // Update UI when download is complete
        progressText.innerHTML = '<span>Vídeo pronto para compartilhar!</span>';
        progressBar.style.width = '100%';
        shareButton.style.display = 'block';

        // Handle final share button click
        shareButton.onclick = async () => {
          try {
            if (navigator.share) {
              await navigator.share({
                files: [videoFile],
                title: videoTitle,
                text: 'Confira este vídeo na Sintonizy:',
                url: `${window.location.origin}/zy/${clipId}`
              });
              document.body.removeChild(progressContainer);
              toast.positive(message('📽️ Vídeo compartilhado com sucesso!'));
            } else {
              // Fallback para download direto
              const url = window.URL.createObjectURL(videoBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = data.filename;
              document.body.appendChild(link);
              link.click();
              
              setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }, 100);
              
              document.body.removeChild(progressContainer);
              toast.positive(message('📽️ Download iniciado com sucesso!'));
            }
          } catch (error) {
            console.error('Share error:', error);
            toast.danger(message('⚠️ Erro ao compartilhar o vídeo. Tente novamente.'));
          }
        };
      } catch (error: unknown) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            return; // Already handled
          }
          console.error('Share error:', error);
          document.body.removeChild(progressContainer);
          toast.danger(message('⚠️ Erro ao compartilhar o vídeo. Tente novamente.'));
        }
      }
    };

    // Handle cancel button click
    cancelButton.onclick = () => {
      document.body.removeChild(floatingContainer);
    };

  } catch (error) {
    console.error('Share error:', error);
    toast.danger(message('⚠️ Erro ao iniciar compartilhamento'));
  }
};

  const fetchClips = async (page: number) => {
    try {
      const response = await fetch(`/clips-list.php?page=${page}`);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const data: ClipsResponse = await response.json();
      if (data.status === 'success') {
        return data;
      }
      throw new Error('Falha ao buscar clipes');
    } catch (error) {
      console.error('Error:', error);
      toast.danger(message('Erro ao carregar vídeos'));
      return null;
    }
  };

  // Função de carregamento de mais clipes
const loadMoreClips = useCallback(async () => {
  if (isLoadingMore || !hasMore) return;

  setIsLoadingMore(true);
  const nextPage = currentPage + 1;
  const data = await fetchClips(nextPage);

  if (data) {
    setClips(prevClips => [...prevClips, ...data.clips]); // Concatena novos clipes
    setHasMore(data.hasMore); // Atualiza se há mais clipes
    setCurrentPage(nextPage); // Atualiza a página atual
  }

  setIsLoadingMore(false);
}, [currentPage, hasMore, isLoadingMore]);
// Trigger de carregamento ao atingir o penúltimo vídeo
useEffect(() => {
  if (currentIndex === clips.length - 2 && hasMore) {
    loadMoreClips();
  }
}, [currentIndex, clips.length, hasMore, loadMoreClips]);


  // Check if we need to load more videos
   useEffect(() => {
    const initialLoad = async () => {
      const data = await fetchClips(1); // Inicialmente, busca o primeiro lote de clipes
      if (data) {
        setClips(data.clips);
        setHasMore(data.hasMore);
        setCurrentPage(1);
        videoRefs.current = new Array(data.clips.length).fill(null);
  
        // Se clipId estiver presente nos parâmetros da URL, encontre e defina o índice correto
        if (clipId) {
          const index = data.clips.findIndex(clip => clip.clip_id === clipId);
          if (index !== -1) {
            setCurrentIndex(index);
          } else {
            // Tenta carregar mais clipes até encontrar o clipId
            let page = 2;
            while (data.hasMore && index === -1) {
              const moreData = await fetchClips(page);
              if (moreData) {
                setClips(prev => [...prev, ...moreData.clips]);
                setHasMore(moreData.hasMore);
                setCurrentPage(page);
                const newIndex = moreData.clips.findIndex(clip => clip.clip_id === clipId);
                if (newIndex !== -1) {
                  setCurrentIndex(newIndex + (page - 1) * 5); // Ajusta o índice ao encontrar o clipId
                  break;
                }
                page++;
              }
            }
          }
        }
      }
      setLoading(false);
    };
  
    initialLoad();
  }, [clipId]); // Dependência no `clipId`

   // Update URL when current video changes
   useEffect(() => {
    if (clips.length > 0) {
      const currentClip = clips[currentIndex];
      window.history.replaceState(
        null, 
        '', 
        `/zy/${currentClip.clip_id}`
      );
    }
  }, [currentIndex, clips]);

  // Preload next video
  useEffect(() => {
    const preloadNextVideo = () => {
      const nextIndex = currentIndex + 1;
      if (
        nextIndex < clips.length &&
        !preloadedVideos.current.has(nextIndex) &&
        videoRefs.current[nextIndex]
      ) {
        const video = videoRefs.current[nextIndex];
        if (video) {
          video.load();
          preloadedVideos.current.add(nextIndex);
        }
      }
    };

    preloadNextVideo();
  }, [currentIndex, clips.length]);

  // Handle video playback based on holding state
  useEffect(() => {
    const currentVideo = videoRefs.current[currentIndex];
    if (currentVideo) {
      if (isHolding) {
        currentVideo.pause();
      } else {
        currentVideo.play().catch(() => {
          // Handle autoplay failure silently
        });
      }
    }
  }, [isHolding, currentIndex]);

  const handleVideoTransition = (index: number) => {
    videoRefs.current.forEach((videoRef, i) => {
      if (i === index && videoRef) {
        videoRef.currentTime = 0; // Reset video to start
        videoRef.muted = false; // Enable audio
        videoRef.play().catch(() => {
          // Handle autoplay failure silently
        });
      } else if (videoRef) {
        videoRef.pause();
        videoRef.currentTime = 0;
      }
    });
  };

  const startHoldTimer = () => {
    if (holdTimer) clearTimeout(holdTimer);
    const timer = setTimeout(() => {
      setIsHolding(true);
    }, 200); // 200ms hold time to trigger pause
    setHoldTimer(timer);
  };

  const clearHoldTimer = () => {
    if (holdTimer) {
      clearTimeout(holdTimer);
      setHoldTimer(null);
    }
    setIsHolding(false);
  };

  const handleDrag = (clientY: number, clientX: number) => {
    if (!initialTouchPos.current) return;

    const deltaY = Math.abs(clientY - initialTouchPos.current.y);
    const deltaX = Math.abs(clientX - initialTouchPos.current.x);

    // If movement is mostly horizontal or minimal, treat as a hold
    if (deltaX > deltaY || deltaY < moveThreshold) {
      if (!isHolding) {
        setIsHolding(true);
      }
      return;
    }

    // Clear hold state and handle drag
    clearHoldTimer();
    setIsHolding(false);

    const distance = clientY - (touchStart ?? 0);
    const containerHeight = containerRef.current?.clientHeight || 0;
    const maxDistance = containerHeight * 0.3;
    
    const limitedDistance = Math.max(
      Math.min(distance, maxDistance),
      -maxDistance
    );
    
    setDragDistance(limitedDistance);
    setTouchEnd(clientY);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    initialTouchPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    setTouchStart(e.touches[0].clientY);
    setIsDragging(true);
    startHoldTimer();
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    handleDrag(e.touches[0].clientY, e.touches[0].clientX);
    e.preventDefault();
  };

   // Modified touch end handler to update URL after swipe V3.1
   const onTouchEnd = () => {
    clearHoldTimer();
    setIsDragging(false);
    initialTouchPos.current = null;

    if (!touchStart || !touchEnd) {
      setDragDistance(0);
      return;
    }

    const distance = touchEnd - touchStart;
    const threshold = (containerRef.current?.clientHeight || 0) * 0.2;

    if (Math.abs(distance) > threshold) {
      if (distance < 0 && currentIndex < clips.length - 1) {
        const nextClip = clips[currentIndex + 1];
        window.history.replaceState(
          null, 
          '', 
          `/zy/${nextClip.clip_id}`
        );
        goToNextVideo();
      } else if (distance > 0 && currentIndex > 0) {
        const prevClip = clips[currentIndex - 1];
        window.history.replaceState(
          null, 
          '', 
          `/zy/${prevClip.clip_id}`
        );
        goToPreviousVideo();
      }
    }
    
    setDragDistance(0);
    setTouchStart(null);
    setTouchEnd(null);
  };


  const onMouseDown = (e: React.MouseEvent) => {
    initialTouchPos.current = {
      x: e.clientX,
      y: e.clientY
    };
    setTouchStart(e.clientY);
    setIsDragging(true);
    startHoldTimer();
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    handleDrag(e.clientY, e.clientX);
  };

  const onMouseUp = () => {
    onTouchEnd();
  };

  const onMouseLeave = () => {
    if (isDragging) {
      onTouchEnd();
    }
  };

   // Modified video transition functions to update URL V3.1
   const goToNextVideo = () => {
    if (currentIndex < clips.length - 1) {
      setCurrentIndex(prev => {
        handleVideoTransition(prev + 1);
        const nextClip = clips[prev + 1];
        window.history.replaceState(
          null, 
          '', 
          `/zy/${nextClip.clip_id}`
        );
        return prev + 1;
      });
      setDragDistance(0);
    }
  };

  const goToPreviousVideo = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => {
        handleVideoTransition(prev - 1);
        const prevClip = clips[prev - 1];
        window.history.replaceState(
          null, 
          '', 
          `/zy/${prevClip.clip_id}`
        );
        return prev - 1;
      });
      setDragDistance(0);
    }
  };

  const getVideoTransform = (index: number) => {
    if (index === currentIndex) {
      return `translateY(${dragDistance}px)`;
    }
    
    const gap = 2;
    if (index === currentIndex - 1) {
      return `translateY(calc(-100% + ${dragDistance}px + ${gap}px))`;
    }
    if (index === currentIndex + 1) {
      return `translateY(calc(100% + ${dragDistance}px - ${gap}px))`;
    }
    
    return index < currentIndex ? 'translateY(-100%)' : 'translateY(100%)';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white" />
      </div>
    );
  }

    // renderização dos vídeos 
    return (
      <div className="fixed inset-0 flex justify-center">
      <div 
        className="relative h-full md:h-auto md:aspect-[9/16] md:max-h-screen"
        style={{ width: 'min(100vw, calc(100vh * 9/16))' }}
      >
        {/* Header container - agora dentro do container principal */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-center h-14 bg-gradient-to-b from-black/50 to-transparent">
          <button 
            onClick={handleBackClick}
            style={{ marginTop: '40px' }}
            className="absolute left-4 text-white hover:opacity-80 transition-opacity"
          >
            <ArrowBackIcon style={{ width: '25px', height: '25px' }} />
          </button>
          <h1  style={{ marginTop: '40px' }} className="text-white text-lg font-semibold mt-2.5">
            Clips
          </h1>
        </div>

        {/* Container principal do conteúdo */}
        <div 
          ref={containerRef}
          className="relative h-full w-full"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
        >
          {clips.map((clip, index) => {
            const isVisible = Math.abs(index - currentIndex) <= 1;
            const videoUrl = clip.resolution_854x480 || clip.resolution_1280x720 || clip.video;
            const isLiked = likedVideos.has(clip.id);
            
            return (
              <div
                key={clip.id}
                className="absolute inset-0 transition-transform duration-300 ease-out will-change-transform"
                style={{
                  transform: getVideoTransform(index),
                  opacity: isVisible ? 1 : 0,
                  pointerEvents: isVisible ? 'auto' : 'none',
                  zIndex: index === currentIndex ? 2 : 1
                }}
              >

          {clips.length > 0 && (
            <VideoHelmet
              clipId={clips[currentIndex].clip_id}
              title={clips[currentIndex].title}
              description={clips[currentIndex].description}
              videoUrl={clips[currentIndex].video}
              thumbnail={clips[currentIndex].thumbnail} // Added thumbnail
            />
          )}
                <video
                  ref={el => videoRefs.current[index] = el}
                  src={videoUrl}
                  className="h-full w-full object-contain bg-black"
                  playsInline
                  loop
                  preload="auto"
                  muted={index !== currentIndex}
                  autoPlay={index === currentIndex}
                  onTimeUpdate={() => handleTimeUpdate(index)}
                  onError={() => toast.danger(message('Error playing video'))}
                />

                {/* Video Progress Bar */}
                {isVisible && index === currentIndex && (
                  <div className="absolute bottom-0 left-0 right-0 z-20">
                    <VideoProgress
                      currentTime={videoTimes[index]?.current || 0}
                      duration={videoTimes[index]?.duration || 0}
                      onSeek={(time) => handleSeek(index, time)}
                    />
                  </div>
                )}
                               
                {/* User info and description - Left side */}
                <div style={{ bottom: '2.5rem', left: '1rem', right: '2.8rem' }} className="absolute z-10">
                  <div 
                      className="flex items-center gap-3 mb-3 cursor-pointer" 
                      onClick={() => navigateToUserProfile(clip.user_id, clip.username)}
                      style={{ width: 'fit-content' }} // Garante que a largura seja apenas o necessário

                    >
                                  
                      <img 
                        src={clip.avatar} 
                        alt={clip.username}
                        className="w-[35px] h-[35px] rounded-full hover:opacity-80 transition-opacity"
                      />
                      <span className="text-white font-semibold hover:underline whitespace-nowrap">
                        {clip.username}
                      </span>
                  </div>
                  
                  <h3 
                    className="text-white font-bold mb-2"
                    onClick={() => setExpandedTitle(
                      expandedTitle === clip.id ? null : clip.id
                    )}
                  >
                    {expandedTitle === clip.id 
                      ? clip.title 
                      : truncateText(clip.title, 30)}
                  </h3>
                  
                  <div 
                    className="text-white text-sm"
                    onClick={() => setExpandedDescription(
                      expandedDescription === clip.id ? null : clip.id
                    )}
                  >
                    {expandedDescription === clip.id 
                      ? clip.description 
                      : truncateText(clip.description, 100)}
                  </div>
                </div>
  
                {/* Actions - Right side */}
                <div style={{ bottom: '5rem', right: '0.8rem', gap: '0.575rem' }} className="absolute flex flex-col items-center">
                  <div className="flex flex-col items-center">
                    <button 
                      onClick={() => handleLike(clip.id)} 
                      className="p-2 text-white hover:text-red-500 transition-colors"
                    >
                      {isLiked ? (
                        <FavoriteIcon className="w-[25px] h-[25px] text-red-500" />
                      ) : (
                        <FavoriteBorderIcon className="w-[25px] h-[25px]" />
                      )}
                    </button>
                    <span className="text-white text-xs">{clip.likes}</span>
                  </div>
  
                  <div className="flex flex-col items-center">
                    <div className="p-2 text-white">
                      <VisibilityIcon className="w-[25px] h-[25px]" />
                    </div>
                    <span className="text-white text-xs">{clip.views}</span>
                  </div>
  
                  <div className="flex flex-col items-center">
                  <button 
                    onClick={() => {
                      setActiveVideoId(clip.id);
                      setIsCommentsOpen(true);
                    }}
                    className="p-2 text-white hover:text-blue-500 transition-colors"
                  >
                    <MessageIcon className="w-[25px] h-[25px]" />
                  </button>
                    <span className="text-white text-xs">{clip.comments}</span>
                  </div>
  
                  <div className="flex flex-col items-center">
                  <button 
                      onClick={() => handleShare(clip.clip_id, clip.title, clip.size)} // Add videoSize to your clip interface
                      className="p-2 text-white hover:text-green-500 transition-colors"
                    >
                      <ShareIcon className="w-[25px] h-[25px]" />
                    </button>
                    <span className="text-white text-xs">{clip.sharing}</span>
             {/*  Contagem de compartilhamentos <span className="text-white text-xs">{clip.sharing}</span> */} 
                  </div>
                </div>
              </div>
            );
          })}
  
            {/* Navigation controls */}
            <div style={{ right: '-52px' }} className="absolute bottom-20 flex flex-col gap-4 z-10">
            <button
              onClick={goToPreviousVideo}
              disabled={currentIndex === 0}
              style={{  backgroundColor: '#1e2226'}}
              className="w-[45px] h-[45px]  rounded-full hover:bg-white/20 
                      disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200
                      flex items-center justify-center"
            >
              <ArrowUpwardIcon style={{  height: '19px', width: '19px'}} className="text-white" />
            </button>
            <button
              onClick={goToNextVideo}
              disabled={currentIndex === clips.length - 1}
              style={{  backgroundColor: '#1e2226'}}
              className="w-[45px] h-[45px]  rounded-full hover:bg-white/20 
                      disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200
                      flex items-center justify-center"
            >
              <ArrowDownwardIcon style={{  height: '19px', width: '19px'}} className="text-white" />
            </button>
          </div>
        </div>
      </div>
      {/* Comments Drawer */}
        <CommentsDrawer
          isOpen={isCommentsOpen}
          onClose={() => setIsCommentsOpen(false)}
          videoId={activeVideoId || 0}
        />
    </div>
  );

}

export default ClipsListPage;